import { includes, map } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import { getUrlParamsAsObject } from "@tootools/http-utils";
import { useFeature } from "@radarly/core/features/use-cases";
import { useClosePanel } from "@radarly/core/panel/use-cases";
import { useProject } from "@radarly/core/project/context";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Workspace } from "@radarly/models/workspace";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";
import {
  Benchmark_Template,
  Brands_Benchmark_Template,
  Brands_Focus_Template,
  Brands_Overview_Template,
  Campaign_Template,
  Content_Template,
  Customer_Experience_Template,
  Reputation_Template,
} from "../templates";

//list of active template ids, by default templates are in a "coming soon" state
const activeTemplateIds = [
  Benchmark_Template.id,
  Reputation_Template.id,
  Campaign_Template.id,
  Content_Template.id,
  Customer_Experience_Template.id,
];

type Template = {
  id: string;
  url: string;
  title: string;
  description: string;
  comingSoon: boolean;
};

const sortByLocale = (
  templates: Template[],
  locale: Locale = "en_GB"
): Template[] => {
  const collator = new Intl.Collator(locale.split("_")[0], {
    sensitivity: "base",
  });
  return templates.sort((a, b) => collator.compare(a.title, b.title));
};

function sortTemplatesByLocale(
  templates: Template[],
  locale: Locale
): Template[] {
  const active = [];
  const inactive = [];
  templates.forEach((template) => {
    if (template.comingSoon) {
      return inactive.push(template);
    }
    return active.push(template);
  });
  const sortedActives = sortByLocale(active, locale);
  const sortedInactives = sortByLocale(inactive, locale);
  return [...sortedActives, ...sortedInactives];
}

export function useWizardLibrary({
  router,
  workspaceId,
}: {
  router: IRouterService;
  workspaceId: Workspace["id"];
}) {
  const locale = useCurrentUserLocale();
  const project = useProject();
  const onClosePanel = useClosePanel();

  const paramsURL = getUrlParamsAsObject(["search", "searchWorkspaceId"]);

  const campaignAndBrandsTemplatesActivated = useFeature(
    "wizard_campaign_brands_templates"
  );

  const availableTemplates = [
    Benchmark_Template,
    Reputation_Template,
    Content_Template,
    Customer_Experience_Template,
    Campaign_Template,
  ];

  //list of displayed templates (active and coming soon)
  const templates = campaignAndBrandsTemplatesActivated
    ? availableTemplates.concat([
        Brands_Benchmark_Template,
        Brands_Focus_Template,
        Brands_Overview_Template,
      ])
    : availableTemplates;

  const activeTemplates = campaignAndBrandsTemplatesActivated
    ? activeTemplateIds.concat([
        Brands_Benchmark_Template.id,
        Brands_Focus_Template.id,
        Brands_Overview_Template.id,
        Content_Template.id,
        Customer_Experience_Template.id,
        Campaign_Template.id,
      ])
    : activeTemplateIds;

  const templateEntries: Template[] = map(templates, (template) => {
    const isInactive = !includes(activeTemplates, template.id);
    return {
      id: template.id,
      url: isInactive
        ? ""
        : router.getWorkspaceUrlFromPage(
            Page.insightPagesWizardEditor,
            project,
            workspaceId,
            { ...paramsURL, template: template.id }
          ),
      title: tr(
        locale,
        "INSIGHT_PAGE_WIZARD::LIBRARY::TEMPLATE_" + template.id + "_TITLE"
      ) as string,
      description: tr(
        locale,
        "INSIGHT_PAGE_WIZARD::LIBRARY::TEMPLATE_" + template.id + "_DESCRIPTION"
      ) as string,
      comingSoon: isInactive,
    };
  });
  const emptyTemplate = {
    id: "empty",
    url: router.getWorkspaceUrlFromPage(
      Page.insightPagesEditor,
      project,
      workspaceId,
      { ...paramsURL }
    ),
    title: tr(locale, "INSIGHT_PAGE_WIZARD::LIBRARY::TEMPLATE_EMPTY_TITLE"),
    description: tr(
      locale,
      "INSIGHT_PAGE_WIZARD::LIBRARY::TEMPLATE_EMPTY_DESCRIPTION"
    ),
  };
  const onClose = () => onClosePanel("template-library");
  const sortedTemplateEntries = sortTemplatesByLocale(templateEntries, locale);

  return {
    emptyTemplate,
    templates: sortedTemplateEntries,
    loading: false,
    error: false,
    templatesTitle: tr(locale, "INSIGHT_PAGE_WIZARD::LIBRARY::TEMPLATES_TITLE"),
    title: tr(locale, "INSIGHT_PAGE_WIZARD::LIBRARY::TITLE"),
    onClose,
  };
}
